/**
 * 付款计划生成器
 * 根据租房信息生成付款计划
 */

/**
 * 计算两个日期之间的天数差
 * @param {String} startDate - 开始日期 yyyy-MM-dd
 * @param {String} endDate - 结束日期 yyyy-MM-dd
 * @returns {Number} 天数差
 */
function getDaysBetween(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

/**
 * 日期加上月份（不考虑每月具体天数，直接加月份）
 * @param {String} dateStr - 日期字符串 yyyy-MM-dd
 * @param {Number} months - 要加的月份数
 * @returns {String} 新日期 yyyy-MM-dd
 */
function addMonths(dateStr, months) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // 直接加月份，保持日期不变
    const newDate = new Date(year, month + months, day);

    const newYear = newDate.getFullYear();
    const newMonth = (newDate.getMonth() + 1).toString().padStart(2, '0');
    const newDay = newDate.getDate().toString().padStart(2, '0');
    return `${newYear}-${newMonth}-${newDay}`;
}

/**
 * 日期减去天数
 * @param {String} dateStr - 日期字符串 yyyy-MM-dd
 * @param {Number} days - 要减去的天数
 * @returns {String} 新日期 yyyy-MM-dd
 */
function subtractDays(dateStr, days) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    date.setDate(date.getDate() - days);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * 日期加上天数
 * @param {String} dateStr - 日期字符串 yyyy-MM-dd
 * @param {Number} days - 要加的天数
 * @returns {String} 新日期 yyyy-MM-dd
 */
function addDays(dateStr, days) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * 格式化金额，保留两位小数
 * @param {Number} amount - 金额
 * @returns {Number} 格式化后的金额
 */
function formatAmount(amount) {
    return Math.round(amount * 100) / 100;
}

/**
 * 生成付款计划
 * @param {Object} rentForm - 租房表单数据
 * @returns {Array} 付款计划列表
 */
export function generatePaymentPlan(rentForm) {
    const {
        startDate,           // 合同起始日
        endDate,             // 合同到期日
        deposit,             // 押金
        rent,                // 房屋租金（月）
        propertyFee,         // 物业费（月）
        rentCycle,           // 租金支付周期：1月付 2季付 3半年付 4年付
        propertyCycle,       // 物业费支付周期
        utilityDeposit,      // 水电气押金
        petDeposit           // 宠物押金
    } = rentForm;

    // 验证必要字段
    if (!startDate || !endDate) {
        throw new Error('请选择合同起始日和到期日');
    }

    if (new Date(endDate) <= new Date(startDate)) {
        throw new Error('合同到期日必须大于合同起始日');
    }

    // 支付周期对应的月份数
    const cycleMonths = {
        1: 1,   // 月付
        2: 3,   // 季付
        3: 6,   // 半年付
        4: 12   // 年付
    };

    const rentCycleMonth = cycleMonths[rentCycle] || 1;
    const propertyCycleMonth = cycleMonths[propertyCycle] || 1;

    // 计算每日租金和物业费
    const dailyRent = formatAmount(rent / 30);
    const dailyPropertyFee = formatAmount(propertyFee / 30);

    const paymentPlan = [];
    let paymentIndex = 1;

    // 第一次付款：合同起始日的后一天
    const firstPaymentDate = addDays(startDate, 1);

    // 第一个周期的结束日期（用于说明）
    const firstRentEndDate = addMonths(startDate, rentCycleMonth);
    const firstPropertyEndDate = addMonths(startDate, propertyCycleMonth);

    const firstRentAmount = rent * rentCycleMonth;
    const firstPropertyAmount = propertyFee * propertyCycleMonth;
    const firstTotalAmount = formatAmount(
        firstRentAmount +
        firstPropertyAmount +
        deposit +
        utilityDeposit +
        petDeposit
    );

    paymentPlan.push({
        id: Date.now() + paymentIndex,
        paymentDate: firstPaymentDate,
        amount: firstTotalAmount,
        status: '待付',  // 修改为待付
        paymentIndex: paymentIndex++,
        description: `首次付款：支付${startDate}至${firstRentEndDate}租金${firstRentAmount.toFixed(2)}元、${startDate}至${firstPropertyEndDate}物业费${firstPropertyAmount.toFixed(2)}元，及押金${deposit.toFixed(2)}元、水电气押金${utilityDeposit.toFixed(2)}元、宠物押金${petDeposit.toFixed(2)}元`
    });

    // 计算需要多少个租金周期
    let currentRentStartDate = startDate;
    let cycleCount = 1;

    while (true) {
        // 当前周期的开始日期
        currentRentStartDate = addMonths(startDate, cycleCount * rentCycleMonth);

        // 如果当前周期的开始日期已经超过或等于合同结束日期，则退出循环
        if (new Date(currentRentStartDate) >= new Date(endDate)) {
            break;
        }

        // 计算付款日期：合同起始日延后X个月，再往前推15天
        const baseDate = addMonths(startDate, cycleCount * rentCycleMonth);
        const paymentDate = subtractDays(baseDate, 15);

        // 计算当前周期的结束日期
        const currentRentEndDate = addMonths(startDate, (cycleCount + 1) * rentCycleMonth);

        // 判断是否是最后一个周期
        const isLastCycle = new Date(currentRentEndDate) >= new Date(endDate);

        let rentAmount = 0;
        let propertyAmount = 0;
        let rentDateRange = '';
        let propertyDateRange = '';

        if (isLastCycle) {
            // 最后一个周期：计算从 currentRentStartDate 到 endDate 的天数
            // 注意：房租从 currentRentStartDate 的第二天开始计算
            const rentStartDate = addDays(currentRentStartDate, 1);
            const remainingRentDays = getDaysBetween(rentStartDate, endDate);
            rentAmount = formatAmount(dailyRent * remainingRentDays);
            rentDateRange = `${rentStartDate}至${endDate}`;

            // 物业费同样处理
            const propertyStartDate = addDays(currentRentStartDate, 1);
            const remainingPropertyDays = getDaysBetween(propertyStartDate, endDate);
            propertyAmount = formatAmount(dailyPropertyFee * remainingPropertyDays);
            propertyDateRange = `${propertyStartDate}至${endDate}`;
        } else {
            // 正常周期
            rentAmount = rent * rentCycleMonth;
            propertyAmount = propertyFee * propertyCycleMonth;

            // 房租覆盖期间：从 currentRentStartDate 的第二天到 currentRentEndDate
            const rentStartDate = addDays(currentRentStartDate, 1);
            rentDateRange = `${rentStartDate}至${currentRentEndDate}`;
            propertyDateRange = `${rentStartDate}至${currentRentEndDate}`;
        }

        const totalAmount = formatAmount(rentAmount + propertyAmount);

        // 生成周期描述
        let description = '';
        if (rentAmount > 0 && propertyAmount > 0) {
            description = `支付${rentDateRange}租金${rentAmount.toFixed(2)}元、${propertyDateRange}物业费${propertyAmount.toFixed(2)}元`;
        } else if (rentAmount > 0) {
            description = `支付${rentDateRange}租金${rentAmount.toFixed(2)}元`;
        } else if (propertyAmount > 0) {
            description = `支付${propertyDateRange}物业费${propertyAmount.toFixed(2)}元`;
        }

        // 如果是最后一期，添加说明
        if (isLastCycle) {
            description += '（最后一期按天计算）';
        }

        paymentPlan.push({
            id: Date.now() + paymentIndex + cycleCount,
            paymentDate: paymentDate,
            amount: totalAmount,
            status: '待付',  // 修改为待付
            paymentIndex: paymentIndex++,
            description: description
        });

        cycleCount++;
    }

    return paymentPlan;
}

/**
 * 验证表单数据
 * @param {Object} rentForm - 租房表单数据
 * @returns {Object} 验证结果 {valid, message}
 */
export function validateRentForm(rentForm) {
    const {
        startDate,
        endDate,
        rent,
        propertyFee,
        rentCycle,
        propertyCycle
    } = rentForm;

    if (!startDate) {
        return { valid: false, message: '请选择合同起始日' };
    }

    if (!endDate) {
        return { valid: false, message: '请选择合同到期日' };
    }

    if (new Date(endDate) <= new Date(startDate)) {
        return { valid: false, message: '合同到期日必须大于合同起始日' };
    }

    if (rent <= 0) {
        return { valid: false, message: '房屋租金必须大于0' };
    }

    if (propertyFee < 0) {
        return { valid: false, message: '物业费不能为负数' };
    }

    if (!rentCycle) {
        return { valid: false, message: '请选择租金支付周期' };
    }

    if (!propertyCycle) {
        return { valid: false, message: '请选择物业费支付周期' };
    }

    if (propertyCycle < rentCycle) {
        return { valid: false, message: '物业费支付周期不能小于租金支付周期' };
    }

    return { valid: true, message: '' };
}